import { memo, useEffect, useState } from "react";
import {
  ChevronDown,
  Menu as MenuIcon,
  X,
} from "lucide-react";
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import oxyDtvmLogoColor from "shared/assets/oxy-dtvm-logo-color.png";
import oxyDtvmLogoWhite from "shared/assets/oxy-dtvm-logo-white.png";
import { siteTemporaryAnnouncement } from "shared/config/site";
import type { NavigationActionItem } from "shared/navigation/types";

interface SiteHeaderProps {
  enableScrollEffect?: boolean;
  deferBrandLogoUntilScrolled?: boolean;
  hideBrandLogoUntilScrolled?: boolean;
  brandAction?: () => void;
  primaryLinks: NavigationActionItem[];
  investorLinks: NavigationActionItem[];
  contactLink: NavigationActionItem;
  clientAccessLink: NavigationActionItem;
}

export const SiteHeader = memo(function SiteHeader({
  enableScrollEffect = false,
  deferBrandLogoUntilScrolled = false,
  hideBrandLogoUntilScrolled = false,
  brandAction,
  primaryLinks,
  investorLinks,
  contactLink,
  clientAccessLink,
}: SiteHeaderProps) {
  const [investorAnchorEl, setInvestorAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const isInvestorDropdownOpen = Boolean(investorAnchorEl);
  const isHeroMode = enableScrollEffect && !hasScrolled;
  const isOverlayVisible = isHeroMode && !isMobileMenuOpen;
  const investorMenuButtonId = "site-header-investor-menu-button";
  const investorMenuId = "site-header-investor-menu";
  const mobileMenuId = "site-header-mobile-menu";

  useEffect(() => {
    if (!enableScrollEffect) {
      setHasScrolled(true);
      return;
    }

    const handleScroll = () => {
      const nextHasScrolled = window.scrollY > 28;

      setHasScrolled((currentValue) =>
        currentValue === nextHasScrolled ? currentValue : nextHasScrolled,
      );
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [enableScrollEffect]);

  const isBrandLogoHidden = hideBrandLogoUntilScrolled && isOverlayVisible;
  const shouldShowWhiteLogo =
    deferBrandLogoUntilScrolled && isOverlayVisible && !isBrandLogoHidden;
  const activeColor = isOverlayVisible ? "#84f24a" : "var(--oxy-primary)";
  const navColor = isOverlayVisible
    ? "rgba(248, 250, 252, 0.92)"
    : "var(--oxy-text-strong)";
  const brandLogo = shouldShowWhiteLogo ? oxyDtvmLogoWhite : oxyDtvmLogoColor;

  const closeMenus = () => {
    setIsMobileMenuOpen(false);
    setInvestorAnchorEl(null);
  };

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const runNavigationAction = (action?: () => void) => {
    closeMenus();
    action?.();
  };

  const renderDesktopLink = (
    label: string,
    onClick: () => void,
    key?: string,
  ) => (
    <Button
      key={key ?? label}
      onClick={onClick}
      sx={desktopButtonSx(navColor, activeColor)}
    >
      {label}
    </Button>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: isOverlayVisible ? "transparent" : "var(--oxy-surface)",
        backgroundImage: "none",
        borderBottom: isOverlayVisible
          ? "1px solid rgba(255, 255, 255, 0.14)"
          : "1px solid var(--oxy-border)",
        boxShadow: isOverlayVisible
          ? "none"
          : enableScrollEffect
            ? "0 8px 20px rgba(15, 23, 42, 0.08)"
            : "none",
        transition:
          "background-color 220ms ease, border-color 220ms ease, box-shadow 220ms ease, color 220ms ease",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 88, lg: 104 },
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: isBrandLogoHidden ? 0 : { xs: 138, sm: 176, lg: 194 },
              mr: isBrandLogoHidden ? 0 : { xs: 2, lg: 3 },
              opacity: isBrandLogoHidden ? 0 : 1,
              overflow: "hidden",
              flexShrink: 0,
              pointerEvents: isBrandLogoHidden ? "none" : "auto",
              transition:
                "width 260ms ease, margin-right 260ms ease, opacity 180ms ease",
            }}
          >
            <Button
              onClick={() => runNavigationAction(brandAction)}
              aria-label="Página inicial OXY DTVM"
              aria-hidden={isBrandLogoHidden}
              tabIndex={isBrandLogoHidden ? -1 : 0}
              sx={{
                p: 0,
                minWidth: 0,
                width: "100%",
                lineHeight: 0,
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <Box
                component="img"
                src={brandLogo}
                alt="OXY DTVM"
                sx={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  filter: isOverlayVisible
                    ? "drop-shadow(0 2px 10px rgba(2, 6, 23, 0.34))"
                    : "none",
                }}
              />
            </Button>
          </Box>

          <Box sx={{ flex: 1, display: { xs: "none", lg: "flex" }, gap: 0.6 }}>
            {primaryLinks.map((item) =>
              renderDesktopLink(
                item.label,
                () => runNavigationAction(item.onClick),
                item.label,
              ),
            )}

            <Button
              id={investorMenuButtonId}
              onMouseEnter={(event) => {
                blurActiveElement();
                setInvestorAnchorEl(event.currentTarget);
              }}
              onClick={(event) => {
                blurActiveElement();
                setInvestorAnchorEl(event.currentTarget);
              }}
              onKeyDown={(event) => {
                if (
                  event.key === "ArrowDown" ||
                  event.key === "Enter" ||
                  event.key === " "
                ) {
                  event.preventDefault();
                  setInvestorAnchorEl(event.currentTarget);
                }
              }}
              aria-haspopup="menu"
              aria-controls={isInvestorDropdownOpen ? investorMenuId : undefined}
              aria-expanded={isInvestorDropdownOpen}
              endIcon={
                <ChevronDown
                  size={16}
                  style={{
                    transform: isInvestorDropdownOpen ? "rotate(180deg)" : "none",
                  }}
                />
              }
              sx={desktopButtonSx(navColor, activeColor)}
            >
              Área do Investidor
            </Button>

            <Menu
              id={investorMenuId}
              anchorEl={investorAnchorEl}
              open={isInvestorDropdownOpen}
              disableScrollLock
              onClose={() => setInvestorAnchorEl(null)}
              MenuListProps={{
                "aria-labelledby": investorMenuButtonId,
                onMouseLeave: () => setInvestorAnchorEl(null),
              }}
              PaperProps={menuPaperProps}
            >
              {investorLinks.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => runNavigationAction(item.onClick)}
                  sx={menuItemSx}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>

            {renderDesktopLink(contactLink.label, () =>
              runNavigationAction(contactLink.onClick),
            )}
          </Box>

          <Box
            sx={{
              display: { xs: "flex", lg: "none" },
              flex: 1,
              justifyContent: "center",
              minWidth: 0,
              px: 0.5,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 24,
                maxWidth: "100%",
                px: 1.2,
                borderRadius: 999,
                border: "1px solid rgba(246, 187, 11, 0.42)",
                bgcolor: "rgba(57, 52, 10, 0.76)",
                color: "#fbbf24",
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
                textTransform: "uppercase",
                fontSize: "0.54rem",
                fontWeight: 800,
                lineHeight: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {siteTemporaryAnnouncement}
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              gap: 0.8,
              ml: "auto",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 34,
                px: 1.7,
                borderRadius: 999,
                border: "1px solid rgba(246, 187, 11, 0.42)",
                bgcolor: "rgba(57, 52, 10, 0.76)",
                color: "#fbbf24",
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
                textTransform: "uppercase",
                fontSize: "0.72rem",
                fontWeight: 800,
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {siteTemporaryAnnouncement}
            </Box>

            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                gap: 0.8,
                alignItems: "center",
              }}
            >
              <Button
                onClick={() => runNavigationAction(clientAccessLink.onClick)}
                sx={clientAccessButtonSx(isOverlayVisible)}
              >
                {clientAccessLink.label}
              </Button>
            </Box>
          </Box>

          <IconButton
            onClick={() => setIsMobileMenuOpen((currentState) => !currentState)}
            aria-label={
              isMobileMenuOpen
                ? "Fechar menu principal"
                : "Abrir menu principal"
            }
            aria-expanded={isMobileMenuOpen}
            aria-controls={mobileMenuId}
            sx={{
              display: { xs: "inline-flex", lg: "none" },
              ml: 0.5,
              color: isOverlayVisible
                ? "var(--oxy-surface-subtle)"
                : "var(--oxy-text-strong)",
            }}
          >
            {isMobileMenuOpen ? <X size={24} /> : <MenuIcon size={24} />}
          </IconButton>
        </Toolbar>

        <Collapse in={isMobileMenuOpen}>
          <Box
            id={mobileMenuId}
            sx={{
              display: { xs: "block", lg: "none" },
              py: 2,
              borderTop: "1px solid var(--oxy-border)",
              bgcolor: "var(--oxy-surface)",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
              <Button
                onClick={() => runNavigationAction(brandAction)}
                sx={mobileButtonSx("var(--oxy-primary)")}
              >
                OXY DTVM
              </Button>
              {primaryLinks.map((item) => (
                <Button
                  key={item.label}
                  onClick={() => runNavigationAction(item.onClick)}
                  sx={mobileButtonSx("var(--oxy-text-strong)")}
                >
                  {item.label}
                </Button>
              ))}

              <Typography sx={mobileSectionLabelSx(false)}>
                Área do Investidor
              </Typography>
              {investorLinks.map((item) => (
                <Button
                  key={item.label}
                  onClick={() => runNavigationAction(item.onClick)}
                  sx={mobileButtonSx("var(--oxy-text-strong)")}
                >
                  {item.label}
                </Button>
              ))}

              <Button
                onClick={() => runNavigationAction(contactLink.onClick)}
                sx={mobileButtonSx("var(--oxy-text-strong)")}
              >
                {contactLink.label}
              </Button>

              <Button
                onClick={() => runNavigationAction(clientAccessLink.onClick)}
                sx={mobileClientAccessButtonSx(false)}
              >
                {clientAccessLink.label}
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Container>
    </AppBar>
  );
});

const desktopButtonSx = (navColor: string, activeColor: string) => ({
  color: navColor,
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.9rem",
  textShadow: "none",
  "&:hover": { color: activeColor },
});

const menuPaperProps = {
  sx: {
    mt: 1,
    minWidth: 260,
    borderRadius: 2,
    border: "1px solid var(--oxy-border)",
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
  },
};

const menuItemSx = {
  py: 1.4,
  fontSize: "0.9rem",
  color: "var(--oxy-text-strong)",
};

const mobileButtonSx = (color: string) => ({
  justifyContent: "flex-start",
  color,
  textTransform: "none",
});

const mobileSectionLabelSx = (isHeroMode: boolean) => ({
  fontSize: "0.85rem",
  color: isHeroMode ? "rgba(248,250,252,0.7)" : "var(--oxy-text-secondary)",
  px: 1.8,
  mt: 1.2,
});

const clientAccessButtonSx = (isOverlayVisible: boolean) => ({
  color: isOverlayVisible ? "var(--oxy-surface-subtle)" : "var(--oxy-primary)",
  border: isOverlayVisible
    ? "1px solid rgba(248, 250, 252, 0.4)"
    : "1px solid rgba(13, 115, 119, 0.24)",
  bgcolor: isOverlayVisible ? "rgba(248, 250, 252, 0.08)" : "var(--oxy-primary-surface)",
  textTransform: "none",
  borderRadius: 999,
  px: 1.8,
  py: 0.8,
  fontSize: "0.86rem",
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: isOverlayVisible ? "rgba(248, 250, 252, 0.14)" : "#dff1f1",
    borderColor: isOverlayVisible ? "#84f24a" : "var(--oxy-primary)",
  },
});

const mobileClientAccessButtonSx = (isHeroMode: boolean) => ({
  justifyContent: "center",
  mt: 0.8,
  color: isHeroMode ? "var(--oxy-surface-subtle)" : "var(--oxy-primary)",
  border: isHeroMode
    ? "1px solid rgba(248, 250, 252, 0.24)"
    : "1px solid rgba(13, 115, 119, 0.18)",
  bgcolor: isHeroMode ? "rgba(248, 250, 252, 0.08)" : "var(--oxy-primary-surface)",
  textTransform: "none",
  borderRadius: 999,
  py: 1.1,
  "&:hover": {
    bgcolor: isHeroMode ? "rgba(248, 250, 252, 0.14)" : "#dff1f1",
  },
});
